<template>
    <Teleport to="body">
        <Transition name="modal">
            <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center" @click.self="close">
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>

                <!-- Modal Container -->
                <div class="flex min-h-screen items-center justify-center p-4">
                    <div class="relative z-50 w-full max-w-4xl rounded-lg bg-white p-6" @click.stop>
                        <!-- Close Button -->
                        <button
                            @click="close"
                            class="absolute top-4 right-4 z-10 rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                            :aria-label="t('common.close')"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <!-- Modal Content -->
                        <div class="max-h-[85vh] overflow-y-auto p-6">
                            <slot></slot>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { useTranslations } from '@/composables/useTranslations';
import { watch } from 'vue';

const { t } = useTranslations();

const props = defineProps<{
    show: boolean;
}>();

const emit = defineEmits<{
    close: [];
}>();

const close = () => {
    emit('close');
};

// Close modal on Escape key
watch(
    () => props.show,
    (newValue) => {
        if (newValue) {
            const handleEscape = (e: KeyboardEvent) => {
                if (e.key === 'Escape') {
                    close();
                }
            };
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';

            return () => {
                document.removeEventListener('keydown', handleEscape);
                document.body.style.overflow = '';
            };
        } else {
            document.body.style.overflow = '';
        }
    },
);
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
    transition: transform 0.3s ease;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
    transform: scale(0.95);
}
</style>
